/**
 * Scoring Utilities
 *
 * Functions for calculating assessment scores and interpreting results
 */

import { SCORE_THRESHOLDS, SCORE_LEVELS } from "../constants";
import type {
  QuestionnaireCategory,
  QuestionnaireLayer,
  QuestionnaireQuestion,
  AssessmentResponse,
} from "../questionnaire.service";

export interface CategoryScore {
  category_id: string;
  category_name: string;
  score: number;
  weight: number;
  weighted_score: number;
}

export interface LayerScore {
  layer_id: string;
  layer_name: string;
  score: number;
  weight: number;
  weighted_score: number;
  categories: CategoryScore[];
}

export interface OverallScore {
  total_score: number;
  structural_readiness_score: number;
  investment_leverage_score: number;
  layers: LayerScore[];
  interpretation: string;
  level: keyof typeof SCORE_LEVELS;
}

/**
 * Calculate score for a single category
 */
export function calculateCategoryScore(
  category: QuestionnaireCategory,
  questions: QuestionnaireQuestion[],
  responses: AssessmentResponse[],
): CategoryScore {
  const categoryQuestions = questions.filter(
    (q) => q.category_id === category.id,
  );
  const categoryResponses = responses.filter((r) =>
    categoryQuestions.some((q) => q.id === r.question_id),
  );

  if (categoryResponses.length === 0) {
    return {
      category_id: category.id,
      category_name: category.name,
      score: 0,
      weight: category.weight_percentage,
      weighted_score: 0,
    };
  }

  // Calculate average response value (1-5 scale) and convert to percentage
  const totalValue = categoryResponses.reduce(
    (sum, r) => sum + r.response_value,
    0,
  );
  const averageValue = totalValue / categoryResponses.length;
  const score = ((averageValue - 1) / 4) * 100; // Convert 1-5 to 0-100

  const weighted_score = (score * category.weight_percentage) / 100;

  return {
    category_id: category.id,
    category_name: category.name,
    score: Math.round(score * 10) / 10, // Round to 1 decimal
    weight: category.weight_percentage,
    weighted_score: Math.round(weighted_score * 10) / 10,
  };
}

/**
 * Calculate score for a layer (aggregates category scores)
 */
export function calculateLayerScore(
  layer: QuestionnaireLayer,
  categories: QuestionnaireCategory[],
  questions: QuestionnaireQuestion[],
  responses: AssessmentResponse[],
): LayerScore {
  const layerCategories = categories.filter((c) => c.layer_id === layer.id);

  const categoryScores = layerCategories.map((category) =>
    calculateCategoryScore(category, questions, responses),
  );

  // Layer score is the weighted average of category scores
  const totalWeightedScore = categoryScores.reduce(
    (sum, cs) => sum + cs.weighted_score,
    0,
  );
  const layerScore = totalWeightedScore; // Already weighted

  const weighted_score = (layerScore * layer.weight_percentage) / 100;

  return {
    layer_id: layer.id,
    layer_name: layer.name,
    score: Math.round(layerScore * 10) / 10,
    weight: layer.weight_percentage,
    weighted_score: Math.round(weighted_score * 10) / 10,
    categories: categoryScores,
  };
}

/**
 * Calculate overall assessment score
 */
export function calculateOverallScore(
  layers: QuestionnaireLayer[],
  categories: QuestionnaireCategory[],
  questions: QuestionnaireQuestion[],
  responses: AssessmentResponse[],
): OverallScore {
  const layerScores = layers.map((layer) =>
    calculateLayerScore(layer, categories, questions, responses),
  );

  // Overall score is the weighted sum of layer scores
  const totalScore = layerScores.reduce(
    (sum, ls) => sum + ls.weighted_score,
    0,
  );

  // Extract specific layer scores
  const structuralReadiness = layerScores.find(
    (ls) => ls.layer_name === "Structural Readiness",
  );
  const investmentLeverage = layerScores.find(
    (ls) => ls.layer_name === "Investment Leverage",
  );

  const level = getScoreLevel(totalScore);
  const interpretation = getScoreInterpretation(totalScore);

  return {
    total_score: Math.round(totalScore * 10) / 10,
    structural_readiness_score: structuralReadiness?.score || 0,
    investment_leverage_score: investmentLeverage?.score || 0,
    layers: layerScores,
    interpretation,
    level,
  };
}

/**
 * Determine score level based on thresholds
 */
export function getScoreLevel(score: number): keyof typeof SCORE_LEVELS {
  if (score >= SCORE_THRESHOLDS.EXCELLENT) return "EXCELLENT";
  if (score >= SCORE_THRESHOLDS.GOOD) return "GOOD";
  if (score >= SCORE_THRESHOLDS.MODERATE) return "MODERATE";
  if (score >= SCORE_THRESHOLDS.LOW) return "LOW";
  return "CRITICAL";
}

/**
 * Get interpretation text for a score
 */
export function getScoreInterpretation(score: number): string {
  const level = getScoreLevel(score);
  return SCORE_LEVELS[level].description;
}

/**
 * Calculate completion percentage
 */
export function calculateCompletionPercentage(
  totalQuestions: number,
  answeredQuestions: number,
): number {
  if (totalQuestions === 0) return 0;
  return Math.round((answeredQuestions / totalQuestions) * 100);
}

/**
 * Check if assessment is complete (all required questions answered)
 */
export function isAssessmentComplete(
  questions: QuestionnaireQuestion[],
  responses: AssessmentResponse[],
): boolean {
  const requiredQuestions = questions.filter((q) => q.is_required);
  const answeredQuestionIds = new Set(responses.map((r) => r.question_id));

  return requiredQuestions.every((q) => answeredQuestionIds.has(q.id));
}

/**
 * Get unanswered required questions
 */
export function getUnansweredQuestions(
  questions: QuestionnaireQuestion[],
  responses: AssessmentResponse[],
): QuestionnaireQuestion[] {
  const answeredQuestionIds = new Set(responses.map((r) => r.question_id));

  return questions
    .filter((q) => q.is_required && !answeredQuestionIds.has(q.id))
    .sort((a, b) => a.display_order - b.display_order);
}
